/**
 * Boiler performance graph extraction.
 * Boiler load (%) = (steam production kg/h ÷ capacity kg/h) × 100
 */
export type BoilerGraphAxisType = 'steamProduction' | 'loadPercent';

export interface BoilerEfficiencyPoint {
  steamProductionKgH: number;
  efficiencyPercent: number;
}

export interface BoilerLoadCurvePoint {
  loadPercent: number;
  steamProductionKgH: number;
  efficiencyPercent: number;
}

export interface BoilerGraphExtractionConfig {
  label: string;
  fileNamePattern?: RegExp;
  manufacturer: string;
  model?: string;
  capacityKgPerHour: number;
  graphAxisType?: BoilerGraphAxisType;
  steamProductionRangeMin?: number;
  steamProductionRangeMax?: number;
  efficiencyCurve?: BoilerEfficiencyPoint[];
  loadCurve?: BoilerLoadCurvePoint[];
  steamAtMaxEfficiencyKgH?: number;
  peakEfficiencyPercent?: number;
  referenceLoadPercent?: number;
}

const AALBORG_20K_CURVE: BoilerEfficiencyPoint[] = [
  { steamProductionKgH: 600, efficiencyPercent: 68 },
  { steamProductionKgH: 2000, efficiencyPercent: 82 },
  { steamProductionKgH: 5000, efficiencyPercent: 87 },
  { steamProductionKgH: 7000, efficiencyPercent: 88 },
  { steamProductionKgH: 8000, efficiencyPercent: 86 },
];

/** KANGRIM Aux. Boiler 20 ton/hr — peak 84.7% @ 50% load → 10 000 kg/h */
const KANGRIM_20T_LOAD_CURVE: BoilerLoadCurvePoint[] = [
  { loadPercent: 12, steamProductionKgH: 2442, efficiencyPercent: 83.1 },
  { loadPercent: 20, steamProductionKgH: 4000, efficiencyPercent: 83.4 },
  { loadPercent: 30, steamProductionKgH: 6000, efficiencyPercent: 84.0 },
  { loadPercent: 40, steamProductionKgH: 8000, efficiencyPercent: 84.4 },
  { loadPercent: 50, steamProductionKgH: 10000, efficiencyPercent: 84.7 },
  { loadPercent: 60, steamProductionKgH: 12000, efficiencyPercent: 84.5 },
  { loadPercent: 70, steamProductionKgH: 14000, efficiencyPercent: 84.1 },
  { loadPercent: 80, steamProductionKgH: 16000, efficiencyPercent: 83.6 },
  { loadPercent: 90, steamProductionKgH: 18000, efficiencyPercent: 82.8 },
  { loadPercent: 100, steamProductionKgH: 20000, efficiencyPercent: 82.0 },
];

/** Order matters: more specific patterns first. */
export const DEFAULT_BOILER_GRAPH_PROFILES: BoilerGraphExtractionConfig[] = [
  {
    label: 'KANGRIM — Aux. Boiler 20 ton/hr',
    fileNamePattern:
      /kangrim|kan[\s._-]*grim|aux[\s._-]*boiler|expected[\s._-]*performance|20[\s._-]*ton/i,
    manufacturer: 'KANGRIM',
    model: 'Aux. Boiler 20 ton/hr',
    capacityKgPerHour: 20000,
    graphAxisType: 'loadPercent',
    loadCurve: KANGRIM_20T_LOAD_CURVE,
  },
  {
    label: 'AALBORG MISSION™ OL — 25 000 kg/h',
    fileNamePattern: /25000|25[\s._-]*000/i,
    manufacturer: 'AALBORG INDUSTRIES',
    model: 'MISSION™ OL 25 000',
    capacityKgPerHour: 25000,
    graphAxisType: 'steamProduction',
    steamProductionRangeMin: 600,
    steamProductionRangeMax: 8000,
    efficiencyCurve: [
      { steamProductionKgH: 8750, efficiencyPercent: 88 },
      { steamProductionKgH: 8000, efficiencyPercent: 87 },
    ],
  },
  {
    label: 'AALBORG MISSION™ OL — 16 000 kg/h',
    fileNamePattern: /16000|16[\s._-]*000/i,
    manufacturer: 'AALBORG INDUSTRIES',
    model: 'MISSION™ OL 16 000',
    capacityKgPerHour: 16000,
    graphAxisType: 'steamProduction',
    steamProductionRangeMin: 600,
    steamProductionRangeMax: 8000,
    efficiencyCurve: [
      { steamProductionKgH: 5600, efficiencyPercent: 88 },
      { steamProductionKgH: 8000, efficiencyPercent: 84 },
    ],
  },
  {
    label: 'AALBORG MISSION™ OL — 12 000 kg/h',
    fileNamePattern: /12000|12[\s._-]*000/i,
    manufacturer: 'AALBORG INDUSTRIES',
    model: 'MISSION™ OL 12 000',
    capacityKgPerHour: 12000,
    graphAxisType: 'steamProduction',
    steamProductionRangeMin: 600,
    steamProductionRangeMax: 8000,
    efficiencyCurve: [
      { steamProductionKgH: 4200, efficiencyPercent: 88 },
      { steamProductionKgH: 8000, efficiencyPercent: 83 },
    ],
  },
  {
    label: 'AALBORG MISSION™ OL — 20 000 kg/h',
    fileNamePattern: /mission[\s._-]*ol|aalborg|20000|20[\s._-]*000/i,
    manufacturer: 'AALBORG INDUSTRIES',
    model: 'MISSION™ OL',
    capacityKgPerHour: 20000,
    graphAxisType: 'steamProduction',
    steamProductionRangeMin: 600,
    steamProductionRangeMax: 8000,
    efficiencyCurve: AALBORG_20K_CURVE,
  },
];

export type BoilerExtractionMethod =
  | 'json'
  | 'sidecar-json'
  | 'vision-api'
  | 'gemini-vision'
  | 'ocr-text'
  | 'filename-profile'
  | 'default-profile';

export interface BoilerGraphExtractionResult {
  profileId: string;
  profileLabel: string;
  manufacturer: string;
  model?: string;
  capacityKgPerHour: number;
  boilerCapacityMtPerHr: number;
  maxEfficiencyPercent: number;
  steamProductionKgHr: number;
  boilerLoadPercent: number;
  extractionMethod: BoilerExtractionMethod;
  sourceNotes: string[];
}

export interface BoilerGraphJsonMetadata {
  label?: string;
  manufacturer?: string;
  model?: string;
  capacityKgPerHour?: number;
  capacity_kg_per_hour?: number;
  steamProductionKgH?: number;
  steam_production_kg_h?: number;
  maxEfficiencyPercent?: number;
  max_efficiency_percent?: number;
  graphAxisType?: BoilerGraphAxisType;
  steamProductionRangeMin?: number;
  steamProductionRangeMax?: number;
  efficiencyCurve?: BoilerEfficiencyPoint[];
  efficiency_curve?: Array<Record<string, number | undefined>>;
  loadCurve?: BoilerLoadCurvePoint[];
  load_curve?: Array<Record<string, number | undefined>>;
}

export function calculateBoilerLoadPercent(
  steamProductionKgH: number,
  capacityKgPerHour: number,
): number {
  if (!capacityKgPerHour || capacityKgPerHour <= 0 || !steamProductionKgH || steamProductionKgH <= 0) {
    return 0;
  }
  return round((steamProductionKgH / capacityKgPerHour) * 100, 1);
}

export function parseCapacityFromFileName(fileName: string): number | null {
  const base = fileName.replace(/\.[^.]+$/, '').toLowerCase();
  const tonMatch = base.match(/(\d{1,2})[\s._-]*ton/i);
  if (tonMatch) {
    return Number(tonMatch[1]) * 1000;
  }
  const kgMatch = base.match(/(\d{4,6})[\s._-]*k?g/i);
  if (kgMatch) {
    return Number(kgMatch[1]);
  }
  const standalone = base.match(
    /(?:^|[^0-9])(20000|25000|16000|12000)(?:[^0-9]|$)/,
  );
  return standalone ? Number(standalone[1]) : null;
}

export function findPeakOnSteamCurve(
  curve: BoilerEfficiencyPoint[],
  rangeMin: number,
  rangeMax: number,
): BoilerEfficiencyPoint | null {
  if (!curve.length) {
    return null;
  }
  const inRange = curve.filter(
    (p) => p.steamProductionKgH >= rangeMin && p.steamProductionKgH <= rangeMax,
  );
  const candidates = inRange.length ? inRange : curve;
  return candidates.reduce((best, p) =>
    p.efficiencyPercent > best.efficiencyPercent ? p : best,
  );
}

export function findPeakOnLoadCurve(
  curve: BoilerLoadCurvePoint[],
): BoilerLoadCurvePoint | null {
  if (!curve.length) {
    return null;
  }
  return curve.reduce((best, p) =>
    p.efficiencyPercent > best.efficiencyPercent ? p : best,
  );
}

function hasPlausibleExplicitPeak(profile: BoilerGraphExtractionConfig): boolean {
  const eff = profile.peakEfficiencyPercent;
  const steam = profile.steamAtMaxEfficiencyKgH;
  return (
    eff != null &&
    eff >= 50 &&
    eff <= 95 &&
    steam != null &&
    steam > 0 &&
    steam <= profile.capacityKgPerHour * 1.05
  );
}

export function deriveMetricsFromProfile(profile: BoilerGraphExtractionConfig): {
  steamProductionKgH: number;
  maxEfficiencyPercent: number;
} {
  if (hasPlausibleExplicitPeak(profile)) {
    return {
      steamProductionKgH: profile.steamAtMaxEfficiencyKgH!,
      maxEfficiencyPercent: profile.peakEfficiencyPercent!,
    };
  }

  if (profile.graphAxisType === 'loadPercent' && profile.loadCurve?.length) {
    const peak = findPeakOnLoadCurve(profile.loadCurve);
    if (peak) {
      return {
        steamProductionKgH: peak.steamProductionKgH,
        maxEfficiencyPercent: peak.efficiencyPercent,
      };
    }
  }

  const rangeMin = profile.steamProductionRangeMin ?? 600;
  const rangeMax = profile.steamProductionRangeMax ?? 8000;
  const curve = profile.efficiencyCurve ?? [];

  if (curve.length) {
    const peak = findPeakOnSteamCurve(curve, rangeMin, rangeMax);
    if (peak) {
      return {
        steamProductionKgH: peak.steamProductionKgH,
        maxEfficiencyPercent: peak.efficiencyPercent,
      };
    }
  }

  return {
    steamProductionKgH: profile.steamAtMaxEfficiencyKgH ?? 0,
    maxEfficiencyPercent: profile.peakEfficiencyPercent ?? 0,
  };
}

/** Screenshot/camera names carry no boiler identity — must use vision, not a default profile. */
export function isGenericBoilerGraphFileName(fileName: string): boolean {
  const base = fileName.replace(/\.[^.]+$/, '').toLowerCase();
  return (
    /^(screenshot|screen[\s._-]*shot|image|img|photo|pic|picture|capture|snap|scan|document|untitled|download)([\s._-]|\d|$)/i.test(
      base,
    ) || /^20\d{2}[- ][0-9]{2}[- ][0-9]{2}/.test(base)
  );
}

export function hasExplicitBoilerProfileMatch(
  fileName: string,
  profiles: BoilerGraphExtractionConfig[] = DEFAULT_BOILER_GRAPH_PROFILES,
): boolean {
  const normalized = fileName.toLowerCase();
  if (
    profiles.some((p) => p.fileNamePattern?.test(normalized))
  ) {
    return true;
  }
  const capacity = parseCapacityFromFileName(fileName);
  if (!capacity) {
    return false;
  }
  return profiles.some((p) => p.capacityKgPerHour === capacity && p.fileNamePattern);
}

export function resolveBoilerGraphProfile(
  fileName: string,
  profiles: BoilerGraphExtractionConfig[] = DEFAULT_BOILER_GRAPH_PROFILES,
): BoilerGraphExtractionConfig | null {
  const normalized = fileName.toLowerCase();

  const matches = profiles
    .filter((p) => p.fileNamePattern?.test(normalized))
    .sort(
      (a, b) =>
        (b.fileNamePattern?.source.length ?? 0) -
        (a.fileNamePattern?.source.length ?? 0),
    );

  if (matches.length) {
    return matches[0];
  }

  const capacity = parseCapacityFromFileName(fileName);
  if (capacity) {
    const byCap = profiles.filter(
      (p) => p.capacityKgPerHour === capacity && p.fileNamePattern,
    );
    if (byCap.length === 1) {
      return byCap[0];
    }
    if (byCap.length > 1) {
      const kangrim = byCap.find((p) => p.manufacturer === 'KANGRIM');
      return kangrim ?? byCap[0];
    }
  }

  return null;
}

export function profileFromJson(meta: BoilerGraphJsonMetadata): BoilerGraphExtractionConfig {
  const capacityKgPerHour =
    meta.capacityKgPerHour ?? meta.capacity_kg_per_hour ?? 20000;

  return {
    label: meta.label ?? 'Uploaded JSON profile',
    manufacturer: meta.manufacturer ?? '',
    model: meta.model,
    capacityKgPerHour,
    graphAxisType: meta.graphAxisType ?? 'steamProduction',
    steamProductionRangeMin: meta.steamProductionRangeMin ?? 600,
    steamProductionRangeMax: meta.steamProductionRangeMax ?? 8000,
    peakEfficiencyPercent:
      meta.maxEfficiencyPercent ?? meta.max_efficiency_percent,
    steamAtMaxEfficiencyKgH:
      meta.steamProductionKgH ?? meta.steam_production_kg_h,
    efficiencyCurve: normalizeSteamCurveJson(meta),
    loadCurve: normalizeLoadCurveJson(meta),
  };
}

export function extractBoilerDataFromProfile(
  profile: BoilerGraphExtractionConfig,
  fileName: string,
  method: BoilerExtractionMethod = 'filename-profile',
): BoilerGraphExtractionResult {
  return buildResult(profile, fileName, method);
}

export function extractBoilerDataFromGraph(
  fileName: string,
  profiles: BoilerGraphExtractionConfig[] = DEFAULT_BOILER_GRAPH_PROFILES,
  method: BoilerExtractionMethod = 'filename-profile',
): BoilerGraphExtractionResult | null {
  const profile = resolveBoilerGraphProfile(fileName, profiles);
  if (!profile) {
    return null;
  }
  const hasPatternMatch = hasExplicitBoilerProfileMatch(fileName, profiles);
  const resolvedMethod = hasPatternMatch ? method : 'filename-profile';
  return buildResult(profile, fileName, resolvedMethod);
}

/** Build result from vision/OCR/JSON metadata (any graph format). */
export function extractBoilerDataFromMetadata(
  meta: BoilerGraphJsonMetadata,
  fileName: string,
  method: BoilerExtractionMethod,
): BoilerGraphExtractionResult | null {
  const capacity = meta.capacityKgPerHour ?? meta.capacity_kg_per_hour;
  if (!capacity) {
    return null;
  }
  const result = buildResult(profileFromJson(meta), fileName, method);
  if (!isPlausibleBoilerExtraction(result)) {
    return null;
  }
  return result;
}

/** Reject OCR misreads (e.g. fuel 10700 kcal, 0% efficiency). */
export function isPlausibleBoilerExtraction(
  result: BoilerGraphExtractionResult,
): boolean {
  if (result.maxEfficiencyPercent < 50 || result.maxEfficiencyPercent > 95) {
    return false;
  }
  if (
    result.steamProductionKgHr > result.capacityKgPerHour ||
    result.steamProductionKgHr < 100
  ) {
    return false;
  }
  if (result.boilerLoadPercent < 1 || result.boilerLoadPercent > 100) {
    return false;
  }
  return true;
}

export function parseBoilerGraphJson(raw: string): BoilerGraphJsonMetadata | null {
  try {
    const parsed = JSON.parse(raw) as BoilerGraphJsonMetadata;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function extractBoilerDataFromJson(
  meta: BoilerGraphJsonMetadata,
  fileName: string,
): BoilerGraphExtractionResult | null {
  return extractBoilerDataFromMetadata(meta, fileName, 'json');
}

/** Load extra profiles from assets/config (string patterns → RegExp). */
export function hydrateBoilerProfilesFromJson(
  raw: Array<Record<string, unknown>>,
): BoilerGraphExtractionConfig[] {
  return raw.map((item, index) => {
    const pattern = item['fileNamePattern'];
    return {
      label: String(item['label'] ?? `Profile ${index + 1}`),
      fileNamePattern:
        typeof pattern === 'string' ? new RegExp(pattern, 'i') : undefined,
      manufacturer: String(item['manufacturer'] ?? ''),
      model: item['model'] as string | undefined,
      capacityKgPerHour: Number(item['capacityKgPerHour'] ?? 20000),
      graphAxisType: item['graphAxisType'] as BoilerGraphAxisType | undefined,
      steamProductionRangeMin: item['steamProductionRangeMin'] as
        | number
        | undefined,
      steamProductionRangeMax: item['steamProductionRangeMax'] as
        | number
        | undefined,
      efficiencyCurve: item['efficiencyCurve'] as
        | BoilerEfficiencyPoint[]
        | undefined,
      loadCurve: item['loadCurve'] as BoilerLoadCurvePoint[] | undefined,
      peakEfficiencyPercent: item['peakEfficiencyPercent'] as number | undefined,
      steamAtMaxEfficiencyKgH: item['steamAtMaxEfficiencyKgH'] as
        | number
        | undefined,
    };
  });
}

export function mergeBoilerProfiles(
  base: BoilerGraphExtractionConfig[],
  extra: BoilerGraphExtractionConfig[],
): BoilerGraphExtractionConfig[] {
  const seen = new Set(base.map((p) => p.label));
  const merged = [...base];
  for (const p of extra) {
    if (!seen.has(p.label)) {
      merged.push(p);
      seen.add(p.label);
    }
  }
  return merged;
}

function buildResult(
  profile: BoilerGraphExtractionConfig,
  fileName: string,
  extractionMethod: BoilerExtractionMethod,
): BoilerGraphExtractionResult {
  const derived = deriveMetricsFromProfile(profile);
  const steamProductionKgHr = derived.steamProductionKgH;
  const maxEfficiencyPercent = derived.maxEfficiencyPercent;
  const boilerCapacityMtPerHr = round(profile.capacityKgPerHour / 1000, 2);

  let boilerLoadPercent: number;
  if (profile.referenceLoadPercent != null) {
    boilerLoadPercent = profile.referenceLoadPercent;
  } else {
    boilerLoadPercent = calculateBoilerLoadPercent(
      steamProductionKgHr,
      profile.capacityKgPerHour,
    );
  }
  boilerLoadPercent = Math.min(100, Math.max(1, boilerLoadPercent || 1));

  const rangeMin = profile.steamProductionRangeMin ?? 600;
  const rangeMax = profile.steamProductionRangeMax ?? 8000;
  const axisNote =
    profile.graphAxisType === 'loadPercent'
      ? 'Load-axis graph (efficiency vs load %)'
      : `Steam-axis range ${rangeMin}–${rangeMax} kg/h`;

  return {
    profileId: profile.model ?? profile.manufacturer,
    profileLabel: profile.label,
    manufacturer: profile.manufacturer,
    model: profile.model,
    capacityKgPerHour: profile.capacityKgPerHour,
    boilerCapacityMtPerHr,
    maxEfficiencyPercent,
    steamProductionKgHr,
    boilerLoadPercent,
    extractionMethod,
    sourceNotes: [
      `File: ${fileName}`,
      `Source: ${extractionMethod}`,
      `Profile: ${profile.label}`,
      `Capacity: ${profile.capacityKgPerHour} kg/h → ${boilerCapacityMtPerHr} MT/hr`,
      axisNote,
      `Peak: ${maxEfficiencyPercent}% @ ${steamProductionKgHr} kg/h steam production`,
      `Boiler load: (${steamProductionKgHr} ÷ ${profile.capacityKgPerHour}) × 100 = ${boilerLoadPercent}%`,
    ],
  };
}

function normalizeSteamCurveJson(
  meta: BoilerGraphJsonMetadata,
): BoilerEfficiencyPoint[] | undefined {
  const raw = meta.efficiencyCurve ?? meta.efficiency_curve;
  if (!raw?.length) {
    return undefined;
  }
  return raw
    .map((row) => {
      const p = row as Record<string, number | undefined>;
      return {
        steamProductionKgH: Number(
          p['steamProductionKgH'] ?? p['steam_production_kg_h'] ?? 0,
        ),
        efficiencyPercent: Number(
          p['efficiencyPercent'] ?? p['efficiency_percent'] ?? 0,
        ),
      };
    })
    .filter((p) => p.steamProductionKgH > 0);
}

function normalizeLoadCurveJson(
  meta: BoilerGraphJsonMetadata,
): BoilerLoadCurvePoint[] | undefined {
  const raw = meta.loadCurve ?? meta.load_curve;
  if (!raw?.length) {
    return undefined;
  }
  return raw
    .map((row) => {
      const p = row as Record<string, number | undefined>;
      return {
        loadPercent: Number(p['loadPercent'] ?? p['load_percent'] ?? 0),
        steamProductionKgH: Number(
          p['steamProductionKgH'] ?? p['steam_production_kg_h'] ?? 0,
        ),
        efficiencyPercent: Number(
          p['efficiencyPercent'] ?? p['efficiency_percent'] ?? 0,
        ),
      };
    })
    .filter((p) => p.loadPercent > 0 && p.steamProductionKgH > 0);
}

function round(value: number, digits: number): number {
  const f = Math.pow(10, digits);
  return Math.round(value * f) / f;
}
